import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Alert,
  Spinner,
  Table
} from 'reactstrap';
import MetaTags from 'react-meta-tags';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import httprequester from '../../lib/httprequester';

const EXPECTED_HEADERS = [
  'user_email',
  'user_name',
  'user_specialist_name',
  'user_handphone',
  'user_password',
  'user_city',
  'Referral',
  'practice_location_type',
  'practice_location_name',
  'str_no',
  'str_name',
  'log_email_agreement',
  'log_wa_agreement'
];

const InjectUser = () => {
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [inserting, setInserting] = useState(false);
  const [insertResult, setInsertResult] = useState(null);
  const [error, setError] = useState('');

  const resetAll = () => {
    setParsedRows([]);
    setParseError('');
    setValidationResult(null);
    setInsertResult(null);
    setError('');
  };

  const handleDownloadTemplate = async () => {
    try {
      const mod = await import('xlsx');
      const XLSX = mod.default || mod;
      const exampleRow = {
        user_email: 'contoh@email.com',
        user_name: 'dr. Contoh Nama',
        user_specialist_name: 'GP',
        user_handphone: '628123456789',
        user_password: 'password123',
        user_city: 'Jakarta',
        Referral: 'TMT12345',
        practice_location_type: 'Rumah Sakit',
        practice_location_name: 'RS Contoh',
        str_no: 'FP00000363830340',
        str_name: 'Contoh Nama',
        log_email_agreement: 'Ya',
        log_wa_agreement: 'Ya'
      };
      const worksheet = XLSX.utils.json_to_sheet([exampleRow], { header: EXPECTED_HEADERS });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
      XLSX.writeFile(workbook, 'template-inject-user.xlsx');
    } catch (err) {
      console.error('Error generating template:', err);
      setError('Gagal membuat file template.');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    resetAll();
    setFileName(file ? file.name : '');
    if (!file) return;

    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (!json.length) {
        setParseError('File tidak berisi data');
        return;
      }

      // Header names from the file may differ in case/whitespace from what the
      // backend expects, so map them onto the canonical column names first.
      const rawHeaders = Object.keys(json[0]);
      const headerMap = {};
      rawHeaders.forEach((h) => {
        const match = EXPECTED_HEADERS.find((eh) => eh.toLowerCase() === h.trim().toLowerCase());
        headerMap[h] = match || h.trim();
      });

      const missingHeaders = EXPECTED_HEADERS.filter((eh) => !Object.values(headerMap).includes(eh));
      if (missingHeaders.length) {
        setParseError(`Kolom berikut tidak ditemukan di file: ${missingHeaders.join(', ')}`);
        return;
      }

      const normalized = json.map((row) => {
        const out = {};
        Object.entries(row).forEach(([k, v]) => {
          out[headerMap[k]] = typeof v === 'string' ? v : String(v ?? '');
        });
        return out;
      });

      setParsedRows(normalized);
    } catch (err) {
      console.error('Error parsing file:', err);
      setParseError('Gagal membaca file. Pastikan format .csv atau .xlsx valid.');
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setError('');
    setValidationResult(null);
    setInsertResult(null);

    try {
      const response = await httprequester.postAsync('admin/user-inject/validate', { rows: parsedRows });
      setValidationResult(response.data.data);
    } catch (err) {
      console.error('Error validating batch:', err);
      setError(err?.response?.data?.message || 'Gagal memvalidasi data');
    } finally {
      setValidating(false);
    }
  };

  const handleCancel = () => {
    setValidationResult(null);
  };

  const handleProceed = async () => {
    if (!validationResult) return;
    const validRows = validationResult.rows.filter((r) => r.valid).map((r) => r.input);
    if (!validRows.length) return;

    setInserting(true);
    setError('');

    try {
      const response = await httprequester.postAsync('admin/user-inject/insert', { rows: validRows });
      setInsertResult(response.data.data);
    } catch (err) {
      console.error('Error inserting batch:', err);
      setError(err?.response?.data?.message || 'Gagal melakukan insert data');
    } finally {
      setInserting(false);
    }
  };

  const invalidRows = validationResult ? validationResult.rows.filter((r) => !r.valid) : [];

  return (
    <div className="page-content">
      <MetaTags>
        <title>Inject User | Ruang Ilmiah CMS</title>
      </MetaTags>
      <Container fluid>
        <Breadcrumbs title="User" breadcrumbItem="Inject User" />

        <Card className="mb-4">
          <CardBody>
            <h5>Upload Data Peserta</h5>
            <div className="mb-3">
              <Button color="link" className="p-0" onClick={handleDownloadTemplate}>
                <i className="bx bx-download me-1"></i>
                Download Template
              </Button>
            </div>

            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
            />

            {parseError && <Alert color="danger" className="mt-3">{parseError}</Alert>}

            {parsedRows.length > 0 && !parseError && (
              <div className="mt-3 d-flex align-items-center gap-2">
                <span>{fileName}: {parsedRows.length} baris terbaca</span>
                <Button color="primary" onClick={handleValidate} disabled={validating}>
                  {validating ? <Spinner size="sm" /> : 'Validasi'}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        {error && <Alert color="danger">{error}</Alert>}

        {validationResult && !insertResult && (
          <Card className="mb-4">
            <CardBody>
              <h5>Hasil Validasi</h5>
              <p>
                Total: {validationResult.total} baris —{' '}
                <span className="text-success">{validationResult.valid_count} valid</span>,{' '}
                <span className="text-danger">{validationResult.invalid_count} gagal</span>
              </p>

              {invalidRows.length > 0 && (
                <>
                  <h6 className="mt-3">Baris Gagal ({invalidRows.length})</h6>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table bordered size="sm" responsive>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Email</th>
                          <th>Nama</th>
                          <th>Alasan Gagal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invalidRows.map((r) => (
                          <tr key={r.row}>
                            <td>{r.row}</td>
                            <td>{r.input.user_email}</td>
                            <td>{r.input.user_name}</td>
                            <td>
                              <ul className="mb-0 ps-3">
                                {r.reasons.map((reason, idx) => (
                                  <li key={idx} className="text-danger">{reason}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              <div className="mt-3 d-flex gap-2">
                <Button color="secondary" onClick={handleCancel} disabled={inserting}>
                  Batal
                </Button>
                <Button
                  color="success"
                  onClick={handleProceed}
                  disabled={inserting || validationResult.valid_count === 0}
                >
                  {inserting ? <Spinner size="sm" /> : `Lanjutkan Insert (${validationResult.valid_count} user valid)`}
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {insertResult && (
          <Card className="mb-4">
            <CardBody>
              <h5>Hasil Insert</h5>
              <p>
                Total diproses: {insertResult.total} —{' '}
                <span className="text-success">{insertResult.inserted_count} berhasil</span>,{' '}
                <span className="text-danger">{insertResult.failed_count} gagal</span>
              </p>

              {insertResult.inserted.length > 0 && (
                <>
                  <h6 className="mt-3">User Berhasil Dibuat ({insertResult.inserted.length})</h6>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table bordered size="sm" responsive>
                      <thead>
                        <tr>
                          <th>user_id</th>
                          <th>Nama</th>
                          <th>Email</th>
                          <th>Membership</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insertResult.inserted.map((u) => (
                          <tr key={u.user_id}>
                            <td>{u.user_id}</td>
                            <td>{u.user_name}</td>
                            <td>{u.user_email}</td>
                            <td>{u.membership}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              {insertResult.failed.length > 0 && (
                <>
                  <h6 className="mt-3 text-danger">Gagal Insert ({insertResult.failed.length})</h6>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Table bordered size="sm" responsive>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Email</th>
                          <th>Alasan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insertResult.failed.map((f) => (
                          <tr key={f.row}>
                            <td>{f.row}</td>
                            <td>{f.email}</td>
                            <td>{(f.reasons || []).join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default InjectUser;
